// Game Selection Page
// Page for selecting and starting a specific Heart Game

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHeartGames } from '../../hooks';
import { useAuthContext } from '../../providers';
import { HeartGame } from '../../types/heartGames';
import {
  HeartGamesContainer,
  HeartGamesHeader,
  HeartGamesTitle,
  HeartGamesSubtitle,
  HeartGamesButton,
  HeartGamesLoading,
  HeartGamesError,
  GameCard,
  GameCardHeader,
  GameCardIcon,
  GameCardTitle,
  GameCardDescription,
  GameCardMeta,
  GameCardBadge
} from '../../styles/HeartGames/Shared.styles';

export const GameSelection: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { games, isLoading, error } = useHeartGames(user?.id);
  const [selectedGame, setSelectedGame] = useState<HeartGame | null>(null);

  useEffect(() => {
    if (gameId && games.length > 0) {
      const game = games.find(g => g.id === gameId);
      setSelectedGame(game || null);
    }
  }, [gameId, games]);

  const handlePlayGame = () => {
    if (selectedGame) {
      navigate(`/heart-games/${selectedGame.id}/play`);
    }
  };

  const handleBack = () => {
    navigate('/heart-games');
  };

  if (isLoading) {
    return (
      <HeartGamesContainer>
        <HeartGamesLoading>Loading game...</HeartGamesLoading>
      </HeartGamesContainer>
    );
  }

  if (error) {
    return (
      <HeartGamesContainer>
        <HeartGamesError>{error}</HeartGamesError>
      </HeartGamesContainer>
    );
  }

  if (!selectedGame) {
    return (
      <HeartGamesContainer>
        <HeartGamesError>Game not found</HeartGamesError>
      </HeartGamesContainer>
    );
  }

  return (
    <HeartGamesContainer>
      <HeartGamesHeader>
        <div>
          <HeartGamesTitle>
            {selectedGame.icon} {selectedGame.name}
          </HeartGamesTitle>
          <HeartGamesSubtitle>
            {selectedGame.description}
          </HeartGamesSubtitle>
        </div>
        <HeartGamesButton variant="outline" onClick={handleBack}>
          ← Back
        </HeartGamesButton>
      </HeartGamesHeader>

      <GameCard style={{ marginBottom: '2rem' }}>
        <GameCardHeader>
          <GameCardIcon color={selectedGame.color}>
            {selectedGame.icon}
          </GameCardIcon>
          <GameCardTitle>{selectedGame.name}</GameCardTitle>
        </GameCardHeader>
        <GameCardDescription>{selectedGame.description}</GameCardDescription>
        <GameCardMeta>
          <GameCardBadge color={selectedGame.color}>
            {selectedGame.difficulty}
          </GameCardBadge>
          <span>{Math.floor(selectedGame.duration / 60)} minutes</span>
          <span>{selectedGame.category}</span>
          <span>{selectedGame.minQuestions}-{selectedGame.maxQuestions} questions</span>
        </GameCardMeta>
      </GameCard>

      {/* Game Details */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          color: 'white', 
          marginBottom: '1rem' 
        }}>
          What to Expect
        </h3>
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: '8px', 
          padding: '1rem' 
        }}>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '1.5rem', 
            color: 'rgba(255, 255, 255, 0.8)' 
          }}>
            <li>Answer {selectedGame.minQuestions}-{selectedGame.maxQuestions} questions</li>
            <li>Complete in approximately {Math.floor(selectedGame.duration / 60)} minutes</li>
            <li>Receive personalized personality insights</li>
            <li>Get compatibility recommendations</li>
            <li>Track your progress and achievements</li>
          </ul>
        </div>
      </div>

      {/* Rewards */}
      {selectedGame.rewards.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            color: 'white', 
            marginBottom: '1rem' 
          }}>
            Rewards
          </h3>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            flexWrap: 'wrap' 
          }}>
            {selectedGame.rewards.map((reward, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}
              >
                {reward.icon && <span style={{ marginRight: '0.5rem' }}>{reward.icon}</span>}
                {reward.description}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'center' 
      }}>
        <HeartGamesButton variant="outline" onClick={handleBack}>
          Cancel
        </HeartGamesButton>
        <HeartGamesButton onClick={handlePlayGame}>
          Start Game
        </HeartGamesButton>
      </div>
    </HeartGamesContainer>
  );
};









